import { ValueFormatter, ValueType } from "src/utils/formatters";
import { CellFormatter, CellFormatterOptions, FormattedCell } from "./types";
import { theme } from "antd";
import styles from "../Table.module.css";
import { TableFormatter } from "../../utils/table-utils";
import { limitWords } from "src/utils/strings";
import { ChangeColor, getChangeColor } from "../../CompareWithControls";
import { getChangeArrowAngle } from "../../CompareWithControls";

//TODO this should get refactored to use the same pattern
//as the tooltip strategy pattern and move into a structure like the
//ComparisonCell.tsx. We want a cell renderer and for each type.

const BASE_OPTIONS: Partial<CellFormatterOptions> = {
  showTooltip: true,
  tooltipPlacement: "top",
  style: "human",
};

const GROUP_OPTIONS: Partial<CellFormatterOptions> = {
  ...BASE_OPTIONS,
  nullValue: "",
  undefinedValue: "",
};

const MEASURE_OPTIONS: Partial<CellFormatterOptions> = {
  ...BASE_OPTIONS,
  nullValue: "-",
  undefinedValue: "-",
};

function shouldShowTooltip(
  value: string,
  options: CellFormatterOptions
): boolean {
  if (!options.showTooltip) return false;
  if (!value) return false;
  // Show tooltip if text is likely to be truncated (more than 20 chars)
  return value.length > 20;
}

/**
 * A group is the first column with the various non-pivot groups of the table.
 * @param value - The value to format
 * @param options - The options for the formatter
 * @param record - The record containing the measure
 * @returns The formatted cell
 */
export const formatGroupCell: CellFormatter = (value, options, record) => {
  const mergedOptions = { ...GROUP_OPTIONS, ...options };

  // Handle null/undefined
  if (value === null)
    return { displayValue: mergedOptions.nullValue!, rawValue: null };
  if (value === undefined)
    return { displayValue: mergedOptions.undefinedValue!, rawValue: undefined };

  // Handle measure name case
  if (record?.measureName) {
    return {
      displayValue: record.measureName,
      rawValue: value,
      alignment: "left",
      className: "table-cell-group",
    };
  }
  //must be a group name...

  let displayValue = TableFormatter.formatDimension(
    value,
    record.field?.attribute
  );
  //must be a group name...

  const result: FormattedCell = {
    displayValue: limitWords(displayValue, 10),
    rawValue: value,
    alignment: "left",
  };

  if (shouldShowTooltip(displayValue, mergedOptions)) {
    result.tooltip = displayValue;
  }

  return result;
};

/**
 * An aggregate cell that for a value like count (issues)
 * @param value - The value to format
 * @param options - The options for the formatter
 * @param record - The record containing the measure
 * @returns The formatted cell
 */
export const formatMeasureCell: CellFormatter = (value, options, record) => {
  const mergedOptions = { ...MEASURE_OPTIONS, ...options };
  const { unit, datatype } = mergedOptions;

  // Handle null/undefined
  if (value === null)
    return { displayValue: mergedOptions.nullValue!, rawValue: null };
  if (value === undefined)
    return { displayValue: mergedOptions.undefinedValue!, rawValue: undefined };

  // Convert string numbers to actual numbers
  const numericValue =
    typeof value === "string" ? Number.parseFloat(value) : value;

  // Get field from either options or record
  const field = mergedOptions.field || record?.measureName;

  // Format the value using ValueFormatter
  const displayValue = ValueFormatter.format(numericValue, {
    field,
    style: mergedOptions.style || "human",
    precision: mergedOptions.precision,
    unit,
    toUnit: mergedOptions.toUnit,
    type: datatype as ValueType,
  });

  const result: FormattedCell = {
    displayValue: displayValue === "0" ? "-" : displayValue,
    rawValue: value,
    alignment: "right",
  };

  if (shouldShowTooltip(displayValue, mergedOptions)) {
    // For measures, show the exact value in tooltip
    result.tooltip =
      typeof numericValue === "number" ? numericValue.toString() : displayValue;
  }

  return result;
};

/**
 * A pivot cell could be an aggregate, group, or formula.
 * @param value - The value to format
 * @param options - The options for the formatter
 * @param record - The record containing the measure
 * @returns The formatted cell
 */
export const formatPivotCell: CellFormatter = (value, options, record) => {
  // Use measure options for measure rows, group options otherwise
  const defaultOptions = record?.measureName ? MEASURE_OPTIONS : GROUP_OPTIONS;
  const mergedOptions = { ...defaultOptions, ...options };

  if (record?.measureName) {
    return formatMeasureCell(
      value,
      {
        ...mergedOptions,
        field: record.measureField,
      },
      record
    );
  }

  return formatGroupCell(value, mergedOptions, record);
};

/**
 * A pivot parent cell is a cell that shows the rollup details for a pivot
 * @param value - The value to format
 * @param options - The options for the formatter
 * @param record - The record containing the measure
 * @returns The formatted cell
 */
export const formatPivotParentCell: CellFormatter = (
  value,
  options,
  record
) => {
  const { token } = theme.useToken();
  const defaultParentCell = {
    displayValue: "",
    rawValue: value,
    ...measureGroupStyle,
    style: { color: token.colorTextDescription },
  };

  if (!options.showRollup) {
    return defaultParentCell;
  }

  const columnKey = options.columnKey;
  if (
    !columnKey ||
    !record.rollupDetails?.[columnKey] ||
    !options.selectedRollupStats
  ) {
    return defaultParentCell;
  }

  const rollupDetails = record.rollupDetails[columnKey];
  const rollupValue = rollupDetails[options.selectedRollupStats];
  const displayValue = ValueFormatter.formatWithUnit(
    rollupValue,
    options.unit,
    options.toUnit
  );

  return {
    ...defaultParentCell,
    displayValue,
    rawValue: rollupValue || value,
  };
};

/**
 * A parent cell has a set of children cells.  It is used to show the rollup details
 * @param value - The value to format
 * @param options - The options for the formatter
 * @param record - The record containing the measure
 * @returns The formatted cell
 */
export const formatParentCell: CellFormatter = (value, options, record) => {
  const { token } = theme.useToken();

  // Return group cell formatting if showRollup is false or undefined
  if (!options.showRollup) {
    return {
      displayValue: "",
      rawValue: value,
      ...measureGroupStyle,
      style: { color: token.colorTextDescription },
    };
  }

  // Get rollup details for this column
  const columnKey = options.columnKey;
  if (!columnKey || !record.rollupDetails?.[columnKey]) {
    return formatGroupCell(value, { ...GROUP_OPTIONS, ...options }, record);
  }

  // Get the rollup details for this column
  const details = record.rollupDetails[columnKey];
  if (!details?.stats) {
    return formatGroupCell(value, { ...GROUP_OPTIONS, ...options }, record);
  }

  const { up = 0, down = 0, same = 0, noData = 0 } = details.stats;
  const total = up + down + same + noData;

  // Create a custom display value with HTML elements using string template
  const displayValue = `
    <div class="${styles.trendSummary}">
      ${
        up > 0
          ? `<span class="${styles.trendItem} ${styles.trendUp}" style="color: ${token.colorSuccess}">${up}<span class="${styles.trendIcon}">↑</span></span>`
          : ""
      }
      ${
        down > 0
          ? `<span class="${styles.trendItem} ${styles.trendDown}" style="color: ${token.colorError}">${down}<span class="${styles.trendIcon}">↓</span></span>`
          : ""
      }
      ${
        same > 0
          ? `<span class="${styles.trendItem} ${styles.trendSame}" style="color: ${token.colorTextDescription}">${same}<span class="${styles.trendIcon}">→</span></span>`
          : ""
      }
      ${
        noData > 0
          ? `<span class="${styles.trendItem} ${styles.trendEmpty}" style="color: ${token.colorTextDescription}">${noData}<span class="${styles.trendIcon}">-</span></span>`
          : ""
      }
    </div>
  `;

  // Use color based on dominant trend for the container
  const style = {
    color:
      up > down
        ? token.colorSuccess
        : down > up
        ? token.colorError
        : token.colorTextDescription,
  };

  return {
    displayValue,
    rawValue: value,
    alignment: "right",
    style,
    isHtml: true, // Add this flag to indicate that displayValue contains HTML
  };
};

// Memoize the style object since it doesn't change
const measureGroupStyle = { alignment: "right", isHtml: false } as const;

/**
 * A measure group cell is a cell that shows the rollup details for a measure
 * @param value - The value to format
 * @param options - The options for the formatter
 * @param record - The record containing the measure
 * @returns The formatted cell
 */
export const formatMeasureGroupCell: CellFormatter = (
  value,
  options,
  record
) => {
  const { token } = theme.useToken();

  const defaultCell = {
    displayValue: "-",
    rawValue: value,
    ...measureGroupStyle,
    style: { color: token.colorTextDescription },
  };

  if (!options.showRollup) {
    return defaultCell;
  }

  const columnKey = options.columnKey;
  if (
    !columnKey ||
    !record.rollupDetails?.[columnKey] ||
    !options.selectedRollupStats
  ) {
    return defaultCell;
  }

  const rollupDetails = record.rollupDetails[columnKey];
  const rollupValue = rollupDetails[options.selectedRollupStats];
  const displayValue = ValueFormatter.formatWithUnit(
    rollupValue,
    options.unit,
    options.toUnit
  );

  return {
    ...defaultCell,
    displayValue,
    rawValue: rollupValue || value,
  };
};

interface ComparisonValue {
  current: number | null;
  comparison: number | null;
  delta: number | null;
  percentage: number | null;
  raw: string;
}

//this is duplicate code form teh ComparisonCell.tsx. I plan to refactor this in the
// next few days (fomous last words) was ok shipping this.
export const formatComparisonCell: CellFormatter = (value, options, record) => {
  const { token } = theme.useToken();
  const mergedOptions = { ...MEASURE_OPTIONS, ...options };
  const {
    showChangeAs = "percentage",
    showChangeValues = true,
    changeColor = ChangeColor.TEXT,
  } = mergedOptions;

  // Handle null/undefined
  if (!value || typeof value !== "object") {
    return { displayValue: mergedOptions.nullValue!, rawValue: null };
  }

  const comparisonValue = value as ComparisonValue;
  const { current, comparison, delta, percentage } = comparisonValue;

  // If we don't have valid values, show n/a
  if (
    current === null ||
    comparison === null ||
    delta === null ||
    percentage === null
  ) {
    return { displayValue: "-", rawValue: value };
  }

  const changeValue = showChangeAs === "percentage" ? percentage : delta;
  const formattedChangeValue =
    changeValue === null ? "0" : changeValue.toFixed(2);
  const angle = delta === null ? 0 : getChangeArrowAngle(delta);
  const color =
    token && changeColor === ChangeColor.TEXT && delta === null
      ? undefined
      : delta === null
      ? undefined
      : getChangeColor(token, changeColor, delta);

  // Create HTML string for the display value
  const displayValue = `
    <div style="display: inline-flex; align-items: center; gap: 4px; font-weight: 400; font-size: 12px;">
      ${
        showChangeValues
          ? `<span style="color: ${token.colorTextSecondary}">${current}</span>`
          : ""
      }
      <span style="
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: ${color || "inherit"};
        background-color: ${color ? `${color}11` : "inherit"};
        border-radius: 3px;
        padding: 0 3px;
      ">
        ${
          angle === 0
            ? ""
            : `<span style="transform: rotate(${angle}deg); display: inline-block;">→</span>`
        }
        <span>${Number(changeValue) > 0 ? "+" : ""}${formattedChangeValue}${
    showChangeAs === "percentage" ? "%" : ""
  }</span>
      </span>
    </div>
  `;

  return {
    displayValue,
    rawValue: value,
    isHtml: true,
    alignment: "right",
  };
};

export const formatters = {
  group: formatGroupCell,
  measure: formatMeasureCell,
  pivot: formatPivotCell,
  parent: formatParentCell,
  "pivot-parent": formatPivotParentCell,
  "measure-group": formatMeasureGroupCell,
  comparison: formatComparisonCell,
};
